import type * as React from "react";

import { cx } from "../../lib/cx";
import { ISOLOGO_SRC } from "../../lib/marca";
import { Icon, type IconName } from "../core/Icon";
import s from "./Wordmark.module.css";

export interface WordmarkProps extends React.ComponentPropsWithoutRef<"span"> {
  /** Con `href` es un enlace (a la landing, al panel). */
  href?: string;
  as?: React.ElementType;
  size?: "sm" | "md" | "lg";
  /** Qué va al lado del nombre. Default `"isologo"`: el isologo real del manual (`ISOLOGO_SRC`,
   *  recortado de `packages/ui/assets/isologo-gradient.png`; nunca se redibuja ni se recolorea).
   *  `true` es el cuadrado accent, que queda sólo como contenedor de un `icon`; `false` nada. */
  mark?: boolean | "isologo";
  /** Un glifo blanco adentro del cuadrado accent (pide `mark` en `true`; no aplica al isologo). */
  icon?: IconName;
  /** El cuadrado con la sombra de marca (`--sh-brand-sm`): sólo en una pantalla pública, nunca en el panel. */
  elevated?: boolean;
}

/** El isologo y "Klavia" en Plus Jakarta Sans Bold. Es lo que va donde no cabe o no existe el lockup. */
export function Wordmark({ href, as, size = "md", mark = "isologo", icon, elevated, className, children = "Klavia", ...rest }: WordmarkProps) {
  const Tag: React.ElementType = as ?? (href ? "a" : "span");
  return (
    <Tag className={cx(s.wordmark, className)} data-size={size} href={href} aria-label={href ? "Klavia" : undefined} {...rest}>
      {mark === "isologo" ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={ISOLOGO_SRC} alt="" className={s.isologo} aria-hidden="true" />
      ) : mark ? (
        <span className={s.marca} data-elevated={elevated || undefined} aria-hidden="true">
          {icon ? <Icon name={icon} size={size === "sm" ? 12 : size === "lg" ? 20 : 16} strokeWidth={2} /> : null}
        </span>
      ) : null}
      <span>{children}</span>
    </Tag>
  );
}
